import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import Sidebar from "../components/Sidebar";
import {
  Bell,
  AlertTriangle,
  Clock,
  Send,
  Flame,
  MessageSquare
} from "lucide-react";

const Alerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Error al cargar alertas");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenMessage = (client) => {
    setSelectedClient(client);
    setMessage(`¡Hola ${client.name.split(' ')[0]}! Te extrañamos por aquí. ¿Cómo te ha ido con tu alimentación estos días? Recuerda que estoy aquí para apoyarte.`);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedClient) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/clients/${selectedClient.client_id}/chat`,
        { content: message },
        { withCredentials: true }
      );
      toast.success("Mensaje enviado");
      setSelectedClient(null);
      setMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const formatLastCheckin = (dateStr) => {
    if (!dateStr) return "Sin check-in";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Hace 1 día";
    return `Hace ${diffDays} días`;
  };

  const getStatusColor = (status) => {
    return status === "yellow" ? "bg-amber-500/10 text-amber-700 border-amber-200" : "bg-red-500/10 text-red-700 border-red-200";
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#FAFAF9]">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="h-8 w-48 skeleton rounded-lg"></div>
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="h-32 skeleton rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAFAF9]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#DC2626]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Alertas</h1>
              <p className="text-stone-500">Clientes que necesitan tu atención</p>
            </div>
          </div>

          {/* Alerts List */}
          {alerts.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-12 text-center">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-[#16A34A]" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">¡Todo en orden!</h3>
              <p className="text-stone-500">No hay clientes en riesgo en este momento.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((client) => (
                <div 
                  key={client.client_id}
                  className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 hover:-translate-y-0.5 hover:shadow-md transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        client.status === "yellow" ? "bg-amber-100" : "bg-red-100"
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          client.status === "yellow" ? "text-amber-600" : "text-red-600"
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900">{client.name}</h3>
                        <p className="text-sm text-stone-500">{client.goal}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(client.status)}`}>
                            {client.status === "yellow" ? "1-2 días sin check-in" : "3+ días sin respuesta"}
                          </span>
                          <div className="flex items-center gap-1 text-sm text-stone-500">
                            <Clock className="w-4 h-4" />
                            <span>{formatLastCheckin(client.last_checkin)}</span>
                          </div>
                          {client.current_streak > 0 && (
                            <div className="flex items-center gap-1 text-sm text-stone-500">
                              <Flame className="w-4 h-4 text-orange-500" />
                              <span>Racha rota en {client.current_streak} días</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/clients/${client.client_id}`)}
                        data-testid={`view-alert-${client.client_id}`}
                      >
                        Ver perfil
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-full bg-[#2563EB] hover:bg-blue-700"
                        onClick={() => handleOpenMessage(client)}
                        data-testid={`message-alert-${client.client_id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Enviar mensaje
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {alerts.length > 0 && (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <MessageSquare className="w-4 h-4" />
                <span>
                  Tienes <strong>{alerts.filter(a => a.status === "red").length}</strong> clientes en riesgo alto y{" "}
                  <strong>{alerts.filter(a => a.status === "yellow").length}</strong> en alerta.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Send Message Modal */}
        <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Enviar mensaje a {selectedClient?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escribe tu mensaje..."
                rows={4}
                data-testid="alert-message-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedClient(null)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSendMessage}
                  disabled={sending || !message.trim()}
                  className="rounded-full bg-[#2563EB] hover:bg-blue-700"
                  data-testid="send-alert-message-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default Alerts;
